import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { Project } from '../data/projects';

type Props = {
  project: Project;
  index?: number;
};

const statusStyles: Record<Project['status'], string> = {
  Live: 'bg-emerald-500/10 text-emerald-300 border-emerald-400/30',
  Concept: 'bg-amber-500/10 text-amber-300 border-amber-400/30',
  'Case study': 'bg-accent-500/10 text-accent-400 border-accent-500/30',
};

export default function ProjectCard({ project, index = 0 }: Props) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: 'easeOut' }}
    >
      <Link
        to={`/projects/${project.slug}`}
        className="card group flex h-full flex-col"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-accent-400">
              {project.category}
            </p>
            <h3 className="mt-2 font-display text-xl font-semibold text-white">
              {project.title}
            </h3>
          </div>
          <span
            className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
              statusStyles[project.status]
            }`}
          >
            {project.status}
          </span>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-white/70">
          {project.summary}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {project.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 font-mono text-[11px] text-white/65"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-6 border-t border-white/10 pt-4 text-sm">
          <p className="line-clamp-3 text-white/55">
            <span className="text-white/80">Outcome — </span>
            {project.outcome}
          </p>
        </div>

        <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-accent-400 opacity-70 transition group-hover:opacity-100">
          Read case study
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            className="h-3.5 w-3.5 transition group-hover:translate-x-0.5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </p>
      </Link>
    </motion.article>
  );
}
